/* ============================================================
   ColdSide — Theme interactions
   Intersection Observer · stat counters · FAQ · gallery ·
   header scroll · mobile nav · variant + bundle selectors
   ============================================================ */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---- Navy overscroll under the footer (11 October 2026) ---------------
     Within 400px of the bottom of the page, <html> takes cs-at-bottom, which
     paints the iPhone bounce area navy to match the footer (coldside.css).
     Passive and throttled to one check a frame. */
  (function () {
    var root = document.documentElement;
    var pending = false;
    function check() {
      pending = false;
      var bottom = (window.scrollY || window.pageYOffset) + window.innerHeight;
      root.classList.toggle('cs-at-bottom', bottom >= root.scrollHeight - 400);
    }
    function queue() {
      if (pending) return;
      pending = true;
      window.requestAnimationFrame(check);
    }
    window.addEventListener('scroll', queue, { passive: true });
    window.addEventListener('resize', queue, { passive: true });
    window.addEventListener('load', queue);
    queue();
  })();

  /* ---- Scroll reveal ---------------------------------------------------- */
  function initReveal() {
    var els = document.querySelectorAll('[class*="anim-"]');
    if (!els.length) return;
    if (reduceMotion || !('IntersectionObserver' in window)) {
      els.forEach(function (el) { el.classList.add('visible'); });
      return;
    }
    var obs = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) {
          e.target.classList.add('visible');
          obs.unobserve(e.target);
        }
      });
    }, { threshold: 0.05, rootMargin: '0px 0px -5% 0px' });
    els.forEach(function (el) { obs.observe(el); });
  }

  /* ---- Stat counters (count up from 0) ---------------------------------- */
  function animateCount(el) {
    var target = parseFloat(el.getAttribute('data-count'));
    if (isNaN(target)) return;
    var decimals = (el.getAttribute('data-decimals') | 0);
    var prefix = el.getAttribute('data-prefix') || '';
    var suffix = el.getAttribute('data-suffix') || '';
    if (reduceMotion) {
      el.textContent = prefix + target.toFixed(decimals) + suffix;
      return;
    }
    var dur = 1500, start = null;
    function step(ts) {
      if (start === null) start = ts;
      var p = Math.min((ts - start) / dur, 1);
      var eased = 1 - Math.pow(1 - p, 3);
      el.textContent = prefix + (target * eased).toFixed(decimals) + suffix;
      if (p < 1) requestAnimationFrame(step);
    }
    requestAnimationFrame(step);
  }

  function initCounters() {
    var els = document.querySelectorAll('[data-count]');
    if (!els.length) return;
    if (!('IntersectionObserver' in window)) {
      els.forEach(animateCount);
      return;
    }
    var obs = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) { animateCount(e.target); obs.unobserve(e.target); }
      });
    }, { threshold: 0.6 });
    els.forEach(function (el) { obs.observe(el); });
  }

  /* ---- FAQ accordion ---------------------------------------------------- */
  function initFaq() {
    document.querySelectorAll('.cs-faq__q').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var item = btn.closest('.cs-faq__item');
        var open = item.classList.toggle('is-open');
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
        var ans = item.querySelector('.cs-faq__a');
        if (ans) ans.style.maxHeight = open ? ans.scrollHeight + 'px' : null;
      });
    });
  }

  /* ---- PDP thumbnail gallery -------------------------------------------- */
  function initGallery() {
    document.querySelectorAll('[data-cs-gallery]').forEach(function (gallery) {
      var main = gallery.querySelector('[data-cs-gallery-main]');
      if (!main) return;
      gallery.querySelectorAll('[data-cs-thumb]').forEach(function (thumb) {
        thumb.addEventListener('click', function () {
          var full = thumb.getAttribute('data-full');
          var alt = thumb.getAttribute('data-alt') || '';
          if (full) { main.src = full; main.alt = alt; }
          gallery.querySelectorAll('[data-cs-thumb]').forEach(function (t) {
            t.classList.remove('is-active');
          });
          thumb.classList.add('is-active');
        });
      });
    });
  }

  /* ---- Header: scroll shadow + mobile nav ------------------------------- */
  function initHeader() {
    var header = document.querySelector('.cs-header');
    if (header) {
      var onScroll = function () {
        header.classList.toggle('is-scrolled', window.scrollY > 4);
      };
      onScroll();
      window.addEventListener('scroll', onScroll, { passive: true });
    }
    var burger = document.querySelector('.cs-burger');
    var mnav = document.querySelector('.cs-mobile-nav');
    if (burger && mnav) {
      burger.addEventListener('click', function () {
        var open = mnav.classList.toggle('is-open');
        burger.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
    }
  }

  /* ---- Bundle selector (radio cards + line item property) --------------- */
  function initBundles() {
    document.querySelectorAll('[data-cs-bundles]').forEach(function (group) {
      group.querySelectorAll('[data-cs-bundle]').forEach(function (card) {
        card.addEventListener('click', function () {
          group.querySelectorAll('[data-cs-bundle]').forEach(function (c) {
            c.classList.remove('is-active');
          });
          card.classList.add('is-active');
          var input = card.querySelector('input[type="radio"]');
          if (input) input.checked = true;
        });
      });
    });
  }

  /* ---- Smooth scroll to reviews ----------------------------------------- */
  function initAnchors() {
    document.querySelectorAll('[data-cs-scroll]').forEach(function (a) {
      a.addEventListener('click', function (e) {
        var id = a.getAttribute('data-cs-scroll');
        var target = document.getElementById(id);
        if (target) { e.preventDefault(); target.scrollIntoView({ behavior: 'smooth' }); }
      });
    });
  }

  function init() {
    initReveal();
    initCounters();
    initFaq();
    initGallery();
    initHeader();
    initBundles();
    initAnchors();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-init in Shopify theme editor when sections reload
  document.addEventListener('shopify:section:load', init);
})();
